import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt, faCopy, faSpinner, faSync, faTrash,
  faCircle, faPaperPlane, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import oastService from '../../services/oastService';

const PAYLOAD_TYPES = [
  { id: 'http', label: 'HTTP Callback', description: 'HTTP/HTTPS callback URL for SSRF testing' },
  { id: 'dns', label: 'DNS Lookup', description: 'DNS resolution callback for blind vulnerabilities' },
  { id: 'ldap', label: 'LDAP', description: 'LDAP callback for JNDI injection (Log4Shell)' },
  { id: 'jndi', label: 'JNDI', description: 'JNDI lookup payload for Java deserialization' },
  { id: 'ssrf', label: 'SSRF', description: 'Server-Side Request Forgery payloads' },
  { id: 'xxe', label: 'XXE', description: 'XML External Entity payloads' },
  { id: 'blind_sqli', label: 'Blind SQLi', description: 'Out-of-band SQL injection payloads' },
  { id: 'cmd_injection', label: 'CMDi', description: 'Command injection with OOB exfiltration' }
];

const OASTPanel = ({ webTarget, isDarkMode }) => {
  const [selectedType, setSelectedType] = useState('http');
  const [generating, setGenerating] = useState(false);
  const [activePayloads, setActivePayloads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (webTarget?._id) loadPayloadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [webTarget]);

  // Auto-poll for callbacks when active payloads exist
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activePayloads.length > 0) {
      setPolling(true);
      pollRef.current = setInterval(pollCallbacks, 3000);
      pollCallbacks();
    } else {
      setPolling(false);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activePayloads]);

  const loadPayloadHistory = async () => {
    try {
      const data = await oastService.getTokens(webTarget._id);
      setActivePayloads(data || []);
    } catch {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await oastService.generatePayload(selectedType, { webTargetId: webTarget._id });
      setActivePayloads(prev => [result, ...prev]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate payload');
    } finally {
      setGenerating(false);
    }
  };

  const pollCallbacks = async () => {
    try {
      const allCallbacks = [];
      for (const payload of activePayloads) {
        if (!payload.token) continue;
        try {
          const cbs = await oastService.pollCallbacks(payload.token);
          if (cbs?.length > 0) {
            allCallbacks.push(...cbs.map(cb => ({ ...cb, token: payload.token, type: payload.type })));
          }
        } catch {}
      }
      if (allCallbacks.length > 0) {
        setCallbacks(prev => {
          const existing = new Set(prev.map(c => `${c.token}:${c.timestamp}`));
          const newOnes = allCallbacks.filter(c => !existing.has(`${c.token}:${c.timestamp}`));
          return [...newOnes, ...prev];
        });
      }
    } catch {}
  };

  const handleDeactivate = async (token) => {
    try {
      await oastService.deactivateToken(token);
      setActivePayloads(prev => prev.filter(p => p.token !== token));
    } catch {}
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const inputClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-3 py-2 border-b ${border} flex items-center gap-2 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <FontAwesomeIcon icon={faBolt} className="text-yellow-400" />
        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          OAST - Out-of-Band Application Security Testing
        </span>
        {polling && (
          <span className="flex items-center gap-1 text-[10px] text-green-400 ml-auto">
            <FontAwesomeIcon icon={faCircle} className="text-[6px] animate-pulse" /> Polling
          </span>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Payload Generator */}
        <div className={`w-80 border-r ${border} flex flex-col overflow-hidden`}>
          <div className={`px-3 py-2 text-[10px] font-medium ${isDarkMode ? 'text-gray-400 bg-gray-800/50' : 'text-gray-500 bg-gray-50/50'}`}>
            PAYLOAD GENERATOR
          </div>

          {/* Type selector */}
          <div className="px-3 py-2 space-y-1.5 overflow-y-auto">
            {PAYLOAD_TYPES.map(pt => (
              <button key={pt.id} onClick={() => setSelectedType(pt.id)}
                className={`w-full text-left px-2.5 py-1.5 rounded text-xs ${
                  selectedType === pt.id
                    ? (isDarkMode ? 'bg-yellow-900/30 border border-yellow-700 text-yellow-300' : 'bg-yellow-50 border border-yellow-300 text-yellow-800')
                    : (isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600')
                }`}>
                <div className="font-medium">{pt.label}</div>
                <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{pt.description}</div>
              </button>
            ))}
          </div>

          <div className={`px-3 py-2 border-t ${border}`}>
            <button onClick={handleGenerate} disabled={generating}
              className="w-full px-3 py-2 bg-yellow-600 text-white rounded text-xs font-medium hover:bg-yellow-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {generating ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBolt} />}
              Generate {PAYLOAD_TYPES.find(p => p.id === selectedType)?.label} Payload
            </button>
            {error && <div className="text-[10px] text-red-400 mt-1">{error}</div>}
          </div>

          {/* Active payloads */}
          <div className={`flex-1 overflow-y-auto border-t ${border}`}>
            <div className={`px-3 py-1.5 text-[10px] font-medium ${isDarkMode ? 'text-gray-400 bg-gray-800/50' : 'text-gray-500 bg-gray-50/50'}`}>
              ACTIVE PAYLOADS ({activePayloads.length})
            </div>
            {activePayloads.map((payload, i) => (
              <div key={payload.token || i} className={`px-3 py-2 border-b ${border}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {payload.type?.toUpperCase() || 'UNKNOWN'}
                  </span>
                  <button onClick={() => handleDeactivate(payload.token)}
                    className="text-[10px] text-red-400 hover:text-red-300">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
                {payload.callbackUrl && (
                  <div className={`text-[10px] font-mono break-all ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {payload.callbackUrl}
                    <button onClick={() => copyToClipboard(payload.callbackUrl)} className="ml-1 text-teal-400 hover:text-teal-300">
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                )}
                {payload.payloads?.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {payload.payloads.slice(0, 3).map((p, j) => (
                      <div key={j} className="flex items-center gap-1">
                        <code className={`text-[9px] flex-1 truncate font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{p}</code>
                        <button onClick={() => copyToClipboard(p)} className="text-[9px] text-teal-400 hover:text-teal-300">
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {activePayloads.length === 0 && (
              <div className={`p-4 text-xs text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                No active payloads. Generate one above.
              </div>
            )}
          </div>
        </div>

        {/* Right: Callback Monitor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`px-3 py-2 flex items-center justify-between ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50/50'} border-b ${border}`}>
            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              CALLBACK MONITOR ({callbacks.length} received)
            </span>
            <button onClick={pollCallbacks} className={`text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {callbacks.length > 0 ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                    <th className="px-3 py-1.5 text-left font-medium">Time</th>
                    <th className="px-3 py-1.5 text-left font-medium">Protocol</th>
                    <th className="px-3 py-1.5 text-left font-medium">Source</th>
                    <th className="px-3 py-1.5 text-left font-medium">Type</th>
                    <th className="px-3 py-1.5 text-left font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {callbacks.map((cb, i) => (
                    <tr key={i} className={`border-b ${border} ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}>
                      <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {cb.timestamp ? new Date(cb.timestamp).toLocaleTimeString() : '-'}
                      </td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        {cb.protocol || cb.type || '-'}
                      </td>
                      <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {cb.sourceIp || '-'}{cb.sourcePort ? `:${cb.sourcePort}` : ''}
                      </td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                        {cb.matchedVulnType || '-'}
                      </td>
                      <td className={`px-3 py-2 font-mono truncate max-w-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {cb.requestData || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={`flex flex-col items-center justify-center h-full text-center p-8 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                <FontAwesomeIcon icon={faBolt} className="text-3xl mb-3" />
                <div className="text-sm font-medium mb-1">No callbacks received yet</div>
                <div className="text-xs">Generate OAST payloads and inject them into requests.<br/>Callbacks will appear here in real-time.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OASTPanel;
